/*============================================================================*/

/**
 * Rendering functions
 *
 * Handles rendering of content into the DOM,
 * whether the content is pre-built or generated elsewhere,
 * centralizing and standardizing DOM insertion logic to avoid
 * unnecessary code repetition across the project.
 */

/*============================================================================*/


/**
 * Build the full URL to an asset in the theme's assets folder
 *
 * @param {Object} args - Configuration object
 * @param {string} args.fileName - Name of the asset file
 * @returns {string} Full URL to the asset
 */
function insertAsset({ fileName })
{
    return `${THEME_URL}assets/${fileName}`;
}

/**
 * Insert an SVG into the DOM with scoped IDs and hrefs to avoid collisions
 *
 * @param {Object} args - Configuration object
 * @param {string} args.fileName - Name of the SVG file (without extension)
 * @returns {Promise<string>} The processed SVG content, ready for DOM insertion
 */
async function insertSVG({ fileName })
{
    const svgContent = await getSVGContent({ fileName });

    if (!svgContent) return '';

    const prefix = `${fileName}__${Math.random().toString(36).substring(2, 9)}`;

    let scopedSVG = svgContent;

    scopedSVG = scopedSVG.replace(/id="([^"]+)"/g, (match, id) => `id="${prefix}__${id}"`);
    scopedSVG = scopedSVG.replace(/url\(#([^)]+)\)/g, (match, id) => `url(#${prefix}__${id})`);
    scopedSVG = scopedSVG.replace(/(xlink:href|href)="#([^"]+)"/g, (match, attr, id) => `${attr}="#${prefix}__${id}"`);

    return scopedSVG;
}

/**
 * Renders an array of posts into a container with optional AOS animation
 *
 * @param {Object} options
 * @param {Array} [options.postsData=[]] - Array of post objects to render.
 * @param {string|Element|jQuery} options.targetElement - Selector or element where posts will be rendered.
 * @param {boolean} [options.shouldAppend=false] - Whether to shouldAppend instead of replacing content.
 */
function renderPosts({ postsData = [], targetElement, shouldAppend = false, type = 'Compact', color = 'Primary' } = {})
{
    const numCols = 3
    const baseDelay = 100
    let delay = 0

    const target = jQuery(targetElement)
    if (!shouldAppend) target.empty()

    if (Array.isArray(postsData) && postsData.length > 0)
    {
        postsData.forEach((post, index) => {
            post.type = type
            post.color = color
            post.cta = 'Read more'
            post.icon = 'east'
            post.aosAnimation = 'fade-up'
            post.aosDelay = (index + 1) * 100
        
            const postEl = Comp_NewCard(post)
            target.append(postEl)
        })
    }
    else
    {
        const emptyState = document.createElement('div')
        emptyState.className = 'cont col fullw h-cent v-cent'

        const msg = document.createElement('h1')
        msg.className = 'ttl-1 ark d-on-surface-cl fcent'
        msg.textContent = 'Coming Soon'

        emptyState.append(msg)
        target.append(emptyState)
    }

    AOS.refresh()
}

/**
 * Renders a catalog of interactive resources into a target container.
 *
 * @param {Object} options
 * @param {Array} options.catalogData - Array of resource objects to render.
 * @param {string|Element|jQuery} options.containerSelector - Selector or element for rendering.
 * @param {string} [options.noResultsMessage=''] - HTML to display if catalogData is empty.
 * @param {number} [options.fadeDuration=300] - Duration for fade-in effect (ms).
 * @param {string} [options.size=''] - CSS size classes for cards.
 * @param {boolean} [options.append=false] - Whether to append to existing content.
 * @param {string} [options.type='Default'] - Type of interaction (e.g., 'Download', 'Default').
 * @param {boolean} [options.modal=true] - Whether to enable modal on click.
 * @param {Object} [options.aos={}] - AOS animation options: delay, interval, etc.
 * 
 * @returns {boolean} True if catalog rendered successfully, false otherwise.
 */
function renderResourcesCatalog({
    catalogData = [],
    containerSelector,
    noResultsMessage = '',
    fadeDuration = 300,
    size = '',
    class_outer = '',
    append = false,
    type = 'Default',
    modal = true,
    aos = {}
} = {})
{
    if (!Array.isArray(catalogData) || catalogData.length === 0)
    {
        if (!append)
        {
            jQuery(containerSelector).html(noResultsMessage);
        }
        return false;
    }

    const sizeClasses = size.split(' ');
    let catalogHtml = '';
    let urlsToPreload = [];

    catalogData.forEach((item, index) => 
    {
        const baseDelay = item.aos?.delay ?? aos.delay ?? 0;
        const finalDelay = baseDelay + ((aos.interval ?? 0) * index);
        const contentType = item.contentType || 'miniature';
        
        const fullArtUrl = item.contentFullArt || '';

        if (fullArtUrl && modal && item.contentExhibition === 'unlocked') 
        {
            urlsToPreload.push(fullArtUrl);
        }

        const args = {
            resourceContainer: `${contentType}Container`,
            resourceSubContainer: `${contentType}SubContainer`,
            resourceMediaContainer: `${contentType}MediaContainer`,
            resourceMedia: `${contentType}Media`,
            resourcePageURL: `${contentType}PageURL`,
            size,
            class_outer,
            resourceName: item.contentTitle || '',
            resourceArt: item.contentArt || '',
            resourceFullArt: fullArtUrl,
            resourceType: contentType,
            resourceExhibition: item.contentExhibition || '',
            resourceFileToDownload: item.contentSTLsURL || '',
            resourcePageURL: item.contentPageURL || '',
            aos:
            {
                ...aos,
                ...item.aos,
                delay: finalDelay
            },
            interactionType: type
        };

        catalogHtml += Comp_InteractiveCard(args);
    });

    if (urlsToPreload.length > 0 && typeof preloadImages === 'function') 
    {
        preloadImages({ urls: urlsToPreload });
    }

    const container = jQuery(containerSelector);
    if (append)
    {
        container.append(catalogHtml);
    }
    else
    {
        container.html(catalogHtml);
    }

    const cards = jQuery(`${containerSelector} > .compInteractiveCard`);
    if (fadeDuration > 0) cards.fadeIn(fadeDuration);
    else cards.show();

    if (modal)
    {
        container.find('.resourceContainer[data-exhibition="unlocked"]').each(function ()
        {
            const container = jQuery(this);
            const hasLink = !!container.attr('href');
    
            if (!hasLink && !container.data('clicked'))
            {
                container.on('click', function () 
                {
                    const resourceName = container.data('resource-name') || '';
                    const resourceType = container.data('resource-type') || '';
                    const resourcePageURL = container.data('resource-page-url') || '';
                    const resourceFileToDownload = container.data('file-to-download') || '';
                    const fullArt = container.data('full-art');

                    const cloned = container.clone();
                    cloned.find('.cardSubContainer, .cardMediaContainer').removeAttr('style');
                    sizeClasses.forEach(cls => cloned.removeClass(cls));
                    cloned.find('h1').remove();
    
                    const img = cloned.find('img');
                    if (img.length) 
                    {
                        sizeClasses.forEach(cls => img.removeClass(cls));
                        
                        img.addClass('onModalResourceImage imageLoading');
                        img.attr('onload', "this.classList.remove('imageLoading'); this.classList.add('imageLoaded');");
        
                        if (fullArt)
                        {
                            img.attr('src', fullArt);
                        }
                    }

                    const titleComp = Comp_Title({
                        title_primary: resourceName,
                        title_size: "3",
                        primary_color: "d-on-surface-cl",
                        font: "ark",
                        text_alignment: "fcent"
                    });

                    let modalContentHtml = `
                        <div class="cont col fullspacing-ph pv-9 g-3 h-cent v-cent">
                            ${(titleComp instanceof HTMLElement) ? titleComp.outerHTML : titleComp}
                            <div class="modalContentContainer cont col fullh g-5 h-cent v-cent">
                                ${cloned.prop('outerHTML')}
                                <div class="cont row h-cent g-2">
                    `;

                    if (type === 'Download' && resourceFileToDownload)
                    {
                        const downloadBtn = Comp_Button({
                            label: "Download",
                            style: "red",
                            frame: "framed",
                            icon_r: "download",
                            class_outer: "co-03 m-co-04",
                            href: resourceFileToDownload,
                            fileName: resourceName
                        });
                        modalContentHtml += (downloadBtn instanceof HTMLElement) ? downloadBtn.outerHTML : downloadBtn;

                        if (resourceType === 'miniature' && resourcePageURL)
                        {
                            const detailsBtn = Comp_Button({
                                label: "See more details",
                                style: "outlined",
                                frame: "framed",
                                class_outer: "co-03 m-co-04",
                                href: resourcePageURL
                            });
                            modalContentHtml += (detailsBtn instanceof HTMLElement) ? detailsBtn.outerHTML : detailsBtn;
                        }
                    }

                    const closeBtn = Comp_Button({
                        style: "outlined",
                        frame: "frameless",
                        icon_r: "close",
                        class_outer: "co-03 m-co-04 CloseModalButton"
                    });
                    modalContentHtml += (closeBtn instanceof HTMLElement) ? closeBtn.outerHTML : closeBtn;

                    modalContentHtml += `
                                </div>
                            </div>
                        </div>
                    `;
    
                    Comp_Modal({
                        content: modalContentHtml,
                        containerClass: `compModal_${type}`
                    });
                });
    
                container.data('clicked', true);
            }
        });
    }

    container.find('.resourceContainer[data-exhibition="locked"]').each(function ()
    {
        const container = jQuery(this);
        const contentType = container.data('type') || 'miniature';
        const mediaContainer = container.find(`.${contentType}MediaContainer`);

        const lockIcon = jQuery('<span>', { class: 'material-symbols-outlined', text: 'lock' });
        const lockContainer = jQuery('<div>', { class: 'lockContainer' }).append(lockIcon);

        mediaContainer.append(lockContainer);
    });

    return true;
}

/**
 * Renders snackbars (temporary notifications) in the DOM.
 * 
 * - Automatically creates the snackbar container if it doesn't exist.
 * - Handles persistent errors, updates existing snackbars, and animates new ones.
 * - Supports optional removal of specific snackbars via `errorsToRemove`.
 * - Ensures proper offset below header menus.
 *
 * @param {Object} options - Configuration object
 * @param {Array<Object>} [options.snackbars=[]] - Array of snackbar objects with properties:
 *      - error: Unique identifier for the snackbar (string)
 *      - type: Optional type of snackbar (e.g., 'error', 'success')
 *      - classes: Optional additional CSS classes
 *      - icon: Optional icon name
 *      - supportingText: Text to display in the snackbar
 *      - confirmingText: Optional secondary text
 *      - url: Optional link to attach
 *      - urlPlacement: 'text' or 'icon', determines where the url applies
 *      - errorsToRemove: Optional array of snackbar errors to remove immediately
 * @param {string} [options.containerSelector='.compSnackbarContainer'] - Selector for the snackbar container
 */
function renderSnackbars({ snackbars = [], containerSelector = '.compSnackbarContainer' } = {}) 
{
    let snackbarContainer = jQuery(containerSelector);

    if (snackbarContainer.length === 0)
    {
        let padding = window.screen.width < 1000 ? 'pv-2 ph-3' : 'pv-5 ph-7';
        snackbarContainer = jQuery(`<div class="cont col ${padding} g-2 v-start h-start belowMenu compSnackbarContainer"></div>`);
        jQuery('body').append(snackbarContainer);

        if (typeof calculateMenuPositions === "function" && typeof applyOffsetBelow === "function") 
        {
            if (typeof LayoutState !== 'undefined' && typeof LayoutState.refreshDynamicTargets === 'function')
            {
                LayoutState.refreshDynamicTargets();
            }

            const checkAndApply = (retryCount = 0) => {
                const layoutData = calculateMenuPositions(); 
                const maxBottom = layoutData.maxBottom;

                applyOffsetBelow(maxBottom, true);
                
                if (maxBottom === 0 && retryCount < 2) 
                {
                    setTimeout(() => checkAndApply(retryCount + 1), 100);
                }
            };
            
            setTimeout(() => checkAndApply(), 50);
        }
    }

    jQuery('.compSnackbar').addClass('persistentError');

    let delay = 0;

    const errorsToRemove = snackbars.find(item => item.errorsToRemove)?.errorsToRemove || [];
    if (errorsToRemove.length > 0)
    {
        errorsToRemove.forEach(errorToRemove => {
            const existingSnackbar = jQuery(`.compSnackbar.${errorToRemove}`);
            if (existingSnackbar.length > 0)
            {
                existingSnackbar.each(function()
                {
                    const snackbar = jQuery(this);
                    snackbar.removeClass('slideInRight').addClass('slideOutRight');
                    snackbar.one('animationend', function() 
                    {
                        snackbar.remove();
                        if (snackbarContainer.children().length === 0) snackbarContainer.remove();
                    });
                });
            }
        });
        return;
    }

    if (snackbars.length > 0)
    {
        snackbars.forEach(args => {
            const { classes, type, icon, error, supportingText, confirmingText, url, urlPlacement } = args;

            const existingSnackbar = jQuery(`.compSnackbar.${error}`);
            
            if (existingSnackbar.length > 0) 
            {
                existingSnackbar.each(function() 
                {
                    const snackbar = jQuery(this);
                    const currentText = snackbar.find('.compSnackbarSupportingText').html();

                    if (!isEmpty(url))
                    {
                        switch (urlPlacement)
                        {
                            case "text":
                                snackbar.find('.textLink').attr('href', url);
                                break;
                            case "icon":
                                snackbar.find('.iconLink').attr('href', url);
                                break;
                        }
                    } 
                    else 
                    {
                        snackbar.find('.textLink, .iconLink').removeAttr('href');
                    }

                    snackbar.removeClass('slideInRight').addClass('shake');

                    if (currentText !== supportingText) 
                        snackbar.find('.compSnackbarSupportingText').html(supportingText);

                    snackbar.removeClass('persistentError');

                    snackbar.one('animationend', function()
                    { 
                        snackbar.removeClass('shake'); 
                    });
                });
            }
            else
            {
                const isDuplicateAnimatingIn = snackbarContainer.find(`.compSnackbar.${error}.slideInRight`).length > 0;
                if (!isDuplicateAnimatingIn)
                {
                    snackbarContainer.append(Comp_SnackBar(args));
                    const newSnackbar = snackbarContainer.find('.compSnackbar:last').addClass('hidden');

                    setTimeout(() => {
                        newSnackbar.removeClass('hidden').addClass('slideInRight');
                    }, delay);

                    delay += 100;
                }
            }
        });
    }

    if (snackbarContainer.children().length > 0)
    {
        jQuery('.compSnackbar.persistentError').each(function()
        {
            const snackbar = jQuery(this);
            snackbar.removeClass('slideInRight').addClass('slideOutRight');
            snackbar.one('animationend', function() { snackbar.remove(); });
        });
    }
    else
    {
        snackbarContainer.remove();
    }
}

// function renderObject3D()
// {
//     const scene = new THREE.Scene();
//     const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);

//     const initialCameraPosition = camera.position.clone();
//     const initialCameraRotation = camera.rotation.clone();

//     const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
//     renderer.setPixelRatio(window.devicePixelRatio);
//     renderer.shadowMap.enabled = true;

//     const container = document.querySelector('#SeekerCardAndMiniature div:first-child');

//     const wrapperDiv = document.createElement('div');
//     wrapperDiv.classList.add("cont", "col", "co-06", "m-co-04", "h-cent", "v-cent", "miniatureDisplay");
//     container.after(wrapperDiv);

//     const subWrapper = document.createElement('div');
//     subWrapper.classList.add("cont", "col", "fullh", "fullw", "h-cent", "v-cent", "miniatureElementsContainer");
//     wrapperDiv.appendChild(subWrapper);

//     renderer.domElement.classList.add("miniatureCanvas");
//     subWrapper.appendChild(renderer.domElement);

//     const ambientLight = new THREE.AmbientLight(0xffffff, 1);
//     scene.add(ambientLight);

//     const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
//     directionalLight.position.set(5, 5, 5);
//     scene.add(directionalLight);

//     const loader = new THREE.GLTFLoader();
//     let size, center;
//     let gltfScene;

//     function fetchGLTF()
//     {
//         return new Promise((resolve, reject) => {
//             const xhr = new XMLHttpRequest();
//             xhr.open('POST', '<?= AjaxURL(); ?>', true);
//             xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');

//             const nonce = wp_create_nonce('ajax_gltf_file_nonce');
//             const fileName = 'CityGuard_32mm_Supported.gltf';
    
//             xhr.onload = function ()
//             {
//                 if (xhr.status === 200) {
//                     const response = JSON.parse(xhr.responseText);
//                     if (response.success)
//                     {
//                         resolve(response.data);
//                     }
//                     else
//                     {
//                         reject(response.data);
//                     }
//                 } else {
//                     reject('Error loading file.');
//                 }
//             };

//             xhr.send(`action=serveGltfFile&nonce=${nonce}&file=${fileName}`);
//         });
//     }

//     fetchGLTF().then(data => {
//         const loader = new THREE.GLTFLoader();
//         loader.parse(data, '', function (gltf)
//         {
//             gltfScene = gltf.scene;
//             const box = new THREE.Box3().setFromObject(gltfScene);
//             size = box.getSize(new THREE.Vector3());
//             center = box.getCenter(new THREE.Vector3());

//             const maxWidth = subWrapper.clientWidth;
//             const scale = Math.min(maxWidth / size.x, 1);
//             gltfScene.scale.set(scale, scale, scale);

//             resetModelAndCameraPosition();

//             scene.add(gltfScene);

//             adjustCameraPosition();
//             adjustSize();
//         }, function (error) {
//             console.error('Fail at loading model:', error);
//         });
//     }).catch(error => {
//         console.error('Error:', error);
//     });

//     const slider = document.createElement('input');
//     slider.type = 'range';
//     slider.min = 0;
//     slider.max = 100;
//     slider.value = 50;
//     slider.classList.add("slider", "miniatureSliderBar");
//     subWrapper.appendChild(slider);

//     const zoomButtonDiv = document.createElement('div');
//     zoomButtonDiv.classList.add("cont", "row", "h-cent", "v-end", "p-3", "g-3", "d-surface-bg", "zoomButtons");

//     const zoomInButton = document.createElement('button');
//     zoomInButton.id = 'MiniatureZoomInButton';
//     zoomInButton.innerHTML = '<span class="material-symbols-outlined">zoom_in</span>';

//     const zoomOutButton = document.createElement('button');
//     zoomOutButton.id = 'MiniatureZoomOutButton';
//     zoomOutButton.innerHTML = '<span class="material-symbols-outlined">zoom_out</span>';

//     zoomButtonDiv.appendChild(zoomInButton);
//     zoomButtonDiv.appendChild(zoomOutButton);
//     subWrapper.appendChild(zoomButtonDiv);

//     const orbitButton = document.createElement('button');
//     orbitButton.id = 'MiniatureOrbitButton';
//     orbitButton.innerText = 'Toggle Orbit';
//     orbitButton.setAttribute('active', 'false');
//     subWrapper.appendChild(orbitButton);

//     let isOrbiting = false;
//     let controls;

//     slider.addEventListener('input', function() {
//         const angle = ((slider.value - 50) / 50) * Math.PI;
//         if (scene.children.length > 2)
//         {
//             scene.children[2].rotation.z = angle;
//         }
//     });

//     zoomInButton.addEventListener('click', function() { camera.position.z *= 0.9; });

//     zoomOutButton.addEventListener('click', function() { camera.position.z *= 1.1; });

//     orbitButton.addEventListener('click', function() {
//         isOrbiting = !isOrbiting;
//         orbitButton.setAttribute('active', isOrbiting ? 'true' : 'false');

//         if (isOrbiting)
//         {
//             slider.style.display = 'none';
//             zoomButtonDiv.style.display = 'none';
//             enableOrbitControl();
//         }
//         else
//         {
//             slider.style.display = 'block';
//             zoomButtonDiv.style.display = 'flex';
//             disableOrbitControl();
//         }
//     });

//     function enableOrbitControl()
//     {
//         controls = new THREE.OrbitControls(camera, renderer.domElement);
//         controls.enableDamping = true;
//         controls.dampingFactor = 0.25;

//         function animateControls()
//         {
//             requestAnimationFrame(animateControls);
//             controls.update();
//         }

//         animateControls();
//     }

//     function disableOrbitControl()
//     {
//         if (controls)
//         {
//             controls.dispose();
//             resetModelAndCameraPosition();
//         }
//     }

//     function resetModelAndCameraPosition()
//     {
//         if (gltfScene)
//         {
//             gltfScene.position.x = -center.x;
//             gltfScene.position.y = -size.y / 2;
//             gltfScene.rotation.x = -Math.PI / 2;

//             const maxWidth = subWrapper.clientWidth;
//             const scale = Math.min(maxWidth / size.x, 1);
//             gltfScene.scale.set(scale, scale, scale);

//             camera.position.copy(initialCameraPosition);
//             camera.rotation.copy(initialCameraRotation);

//             adjustCameraPosition();
//         }
//     }

//     function adjustCameraPosition()
//     {
//         if (size)
//         {
//             const distance = Math.max(size.x, size.y, size.z) * 1.3;
//             camera.position.z = distance;
//         }
//     }

//     function animate()
//     {
//         requestAnimationFrame(animate);
//         renderer.render(scene, camera);
//     }
//     animate();

//     function adjustSize()
//     {
//         const width = wrapperDiv.clientWidth;
//         const height = wrapperDiv.clientHeight;

//         camera.aspect = width / height;
//         camera.updateProjectionMatrix();
//         renderer.setSize(width, height);

//         if (gltfScene)
//         {
//             const box = new THREE.Box3().setFromObject(gltfScene);
//             size = box.getSize(new THREE.Vector3());
//             center = box.getCenter(new THREE.Vector3());

//             adjustCameraPosition();
//         }
//     }

//     window.addEventListener('resize', adjustSize);
// }
/**/